import { useEffect, useState } from 'react'

import { Email } from '@mui/icons-material'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material'

import { handleApiError } from '../../api'
import { sendgridService, type SendGridActivityMessage } from '../../api/services/sendgrid'

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return 'Invalid date'
    return `${date.toISOString().split('T')[0]} ${date.toTimeString().split(' ')[0]?.slice(0, 8)}`
  } catch {
    return 'Invalid date'
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'delivered': return 'success'
    case 'processed': return 'info'
    case 'not_delivered':
    case 'bounced':
    case 'bounce': return 'error'
    case 'deferred': return 'warning'
    case 'dropped': return 'error'
    case 'opened':
    case 'open': return 'success'
    case 'clicked':
    case 'click': return 'success'
    default: return 'default'
  }
}

export function SendGridActivity({ userId }: { userId: string }) {
  const [messages, setMessages] = useState<SendGridActivityMessage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setIsLoading(true)
        setError('')
        const fetched = await sendgridService.getUserActivity(userId, 50)
        setMessages(fetched)
      } catch (err) {
        setError(handleApiError(err))
      } finally {
        setIsLoading(false)
      }
    }
    fetchActivity()
  }, [userId])

  return (
    <Card>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Email fontSize="small" />
          <Typography variant="h6">
            SendGrid Email Activity ({isLoading ? '...' : messages.length})
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="warning">{error}</Alert>
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">No SendGrid email activity found for this user.</Alert>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Opens</TableCell>
                  <TableCell>Clicks</TableCell>
                  <TableCell>From</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map((msg) => (
                  <TableRow key={msg.msgId} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {formatDate(msg.lastEventTime)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {msg.subject || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={msg.status}
                        color={getStatusColor(msg.status) as 'success' | 'info' | 'error' | 'warning' | 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{msg.opensCount}</TableCell>
                    <TableCell>{msg.clicksCount}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {msg.fromEmail}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}
